// The branded email shell, shared by the app (notifications.js) and edge
// functions (through _shared/lib). Runtime-neutral: strings in, a string out.
import { APP_URL, WEBSITE_URL, WEBSITE_HOST } from './org.js';

// The website, not the CRM, in the footer. The CRM is staff-only, and a footer
// read by a parent, an applicant or an agency is a generic invitation, which
// nobody outside should receive to a staff system. CRM links still appear in a
// body where they are a specific action a staff member is asked to take.

// ── Branded HTML email template ───────────────────────────────────────────────
// Two headers. CAERA is the staff system, and its name belongs only on email to
// people who log in to it. Everyone else — applicants, new hires, caregivers
// with no login — gets A Heart For Care: a nurse who reads CAERA across the top
// of her welcome email reasonably goes looking for a CAERA account she does not
// have, and finds the staff login page.
const HEADER = {
  caera: `<table cellpadding="0" cellspacing="0" style="margin:0 auto;">
              <tr>
                <td valign="middle" style="padding-right:14px;">
                  <table cellpadding="0" cellspacing="0" style="background:#1A3A6B;border-radius:9px;" width="46" height="46"><tr><td align="center" valign="middle" width="46" height="46"><img src="${APP_URL}/icons/icon-192.png" alt="CAERA" width="36" height="36" style="display:block;width:36px;height:36px;" /></td></tr></table>
                </td>
                <td valign="middle">
                  <div style="font-family:'Jost',-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;font-size:22px;font-weight:300;color:#F8F6F0;letter-spacing:0.29em;text-transform:uppercase;line-height:46px;">CAERA</div>
                </td>
              </tr>
            </table>`,
  // The cream-on-transparent mark made for the navy login hero. The alt text is
  // styled so a client that blocks images still shows the name.
  ahfc: `<img src="${APP_URL}/ahfc-logo.png" alt="A Heart For Care" width="58" height="85" style="display:block;margin:0 auto;width:58px;height:85px;color:#EBB896;font-size:18px;font-family:Arial,sans-serif;" />`,
};
const FOOTER_NAME = { caera: 'CAERA · A Heart For Care', ahfc: 'A Heart For Care' };

export const emailTemplate = (title, body, ctaText, ctaUrl, accentColor = '#1A3A6B', category = '', footerNote = '', brand = 'caera') => `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${title}</title>
</head>
<body style="margin:0;padding:0;background:#F8F6F0;font-family:'DM Sans',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#F8F6F0;padding:32px 16px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;">

        <!-- Header -->
        <tr>
          <td style="background:#0A1628;border-radius:12px 12px 0 0;padding:22px 32px 18px;text-align:center;">
            ${HEADER[brand]}
          </td>
        </tr>

        <!-- Category badge -->
        ${category ? `
        <tr>
          <td style="background:#0A1628;padding:0 32px 14px;text-align:center;">
            <span style="background:${accentColor};color:#fff;font-size:11px;font-weight:500;padding:3px 12px;border-radius:20px;letter-spacing:0.04em;">${category}</span>
          </td>
        </tr>` : ''}

        <!-- Body -->
        <tr>
          <td style="background:#fff;padding:32px;border-radius:0 0 0 0;">
            <h2 style="color:#0A1628;font-size:20px;font-weight:600;margin:0 0 20px;line-height:1.3;">${title}</h2>
            ${body}
            ${ctaText && ctaUrl ? `
            <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:28px;">
              <tr><td align="center">
                <a href="${ctaUrl}" style="display:inline-block;background:${accentColor};color:#fff;padding:13px 28px;border-radius:8px;text-decoration:none;font-size:14px;font-weight:500;">${ctaText}</a>
              </td></tr>
            </table>` : ''}
          </td>
        </tr>

        <!-- Footer -->
        <tr>
          <td style="background:#F8F6F0;border-top:1px solid #E8E0D0;border-radius:0 0 12px 12px;padding:20px 32px;text-align:center;">
            <div style="color:#45526B;font-size:13px;font-weight:500;margin-bottom:4px;">${FOOTER_NAME[brand]}</div>
            <div style="color:#6B7A94;font-size:12px;margin-bottom:4px;">
              <a href="${WEBSITE_URL}" style="color:#1A3A6B;text-decoration:none;">${WEBSITE_HOST}</a>
            </div>
            <div style="color:#8896AC;font-size:11px;">${footerNote || 'Automated notification — do not reply to this email.'}</div>
          </td>
        </tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;

// For anyone who does not log in to CAERA: families, applicants, candidates,
// new hires.
export const ahfcTemplate = (title, body, ctaText, ctaUrl, accentColor, category, footerNote) =>
  emailTemplate(title, body, ctaText, ctaUrl, accentColor, category, footerNote, 'ahfc');
